import { useState } from "react";
import { describeError, isApiError } from "../api/errors";
import type { Performance, Playlist } from "../api/types";
import { useServices } from "../app/services";
import { announce } from "../state/announcer";
import { Icon } from "./Icon";
import { Dialog } from "./ui";

/**
 * 非公開プレイリストに追加する。並びの版（revision）を一緒に送り、先に変わっていたら取り直す
 */
export function AddToPlaylistButton({ performance }: { performance: Performance }) {
  const { api } = useServices();
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<Playlist[] | null>(null);
  const [status, setStatus] = useState<Record<string, "running" | "done" | string>>({});
  const connected = api.isConnected("playlists.list") && api.isConnected("playlists.edit");
  const suffix = api.mode === "mock" ? "（モック）" : "";

  const openDialog = async () => {
    setOpen(true);
    setStatus({});
    try {
      setLists(await api.getPlaylists());
    } catch (e) {
      setLists([]);
      setStatus({ _: describeError(e) });
    }
  };

  const add = async (pl: Playlist, retry = true): Promise<void> => {
    setStatus((s) => ({ ...s, [pl.id]: "running" }));
    try {
      const updated = await api.editPlaylist(pl.id, {
        name: pl.name,
        revision: pl.revision,
        performance_ids: [...pl.performance_ids, performance.id],
      });
      setLists((ls) => ls?.map((x) => (x.id === pl.id ? updated : x)) ?? null);
      setStatus((s) => ({ ...s, [pl.id]: "done" }));
      announce(`「${pl.name}」に追加しました${suffix}`);
    } catch (e) {
      if (retry && isApiError(e) && e.status === 409) {
        // 先に変わっていた：取り直して、まだ入っていなければもう一度
        const fresh = (await api.getPlaylists()).find((x) => x.id === pl.id);
        setLists((ls) => ls?.map((x) => (x.id === pl.id && fresh ? fresh : x)) ?? null);
        if (fresh && !fresh.performance_ids.includes(performance.id)) return add(fresh, false);
        setStatus((s) => ({ ...s, [pl.id]: "done" }));
        return;
      }
      const msg = describeError(e);
      setStatus((s) => ({ ...s, [pl.id]: msg }));
      announce(msg, "assertive");
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-small"
        onClick={openDialog}
        disabled={!connected}
        title={!connected ? "まだAPIに接続されていません" : undefined}
      >
        <Icon name="queue" className="btn-icon" />
        プレイリストへ
      </button>
      <Dialog open={open} title="プレイリストに追加" onClose={() => setOpen(false)}>
        <p className="dialog__lead">
          「{performance.work.title}」を、自分だけのプレイリストに入れます。プレイリストはほかの人には見えません。
        </p>
        {status._ && <p className="inline-note inline-note--danger">{status._}</p>}
        {lists === null ? (
          <p role="status">読み込み中…</p>
        ) : lists.length === 0 ? (
          <p>プレイリストがありません。ライブラリの「プレイリスト」から作れます。</p>
        ) : (
          <ul className="pl-pick">
            {lists.map((pl) => {
              const inList = pl.performance_ids.includes(performance.id);
              const st = status[pl.id];
              return (
                <li key={pl.id}>
                  <span className="pl-pick__name">
                    {pl.name}
                    <small>{pl.performance_ids.length}曲</small>
                  </span>
                  {inList ? (
                    <span className="pl-pick__in">
                      <Icon name="check" className="inline-icon" />
                      入っています
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      disabled={st === "running"}
                      aria-busy={st === "running" || undefined}
                      onClick={() => add(pl)}
                    >
                      追加
                      <span className="visually-hidden">：{pl.name}</span>
                    </button>
                  )}
                  {st && st !== "running" && st !== "done" && <span className="inline-note inline-note--danger">{st}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </Dialog>
    </>
  );
}
